import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ITrainingFragment, Maybe } from 'graphql/types';
import { SECTIONS } from 'training/constants/sections';
import { TTabsMenuItem } from 'common/types';
import { ITrainingSectionProps } from 'training/types';
import { getTrainingPermissions } from 'training/utils/permissions';

import { useTrainingRoutes } from '.';

interface IState {
    tabs: TTabsMenuItem[];
    Section?: React.FC<React.PropsWithChildren<ITrainingSectionProps>>;
}

export const useTrainingTabs = (
    id?: string,
    training?: Maybe<ITrainingFragment>
): IState => {
    const { activeSectionRoute } = useTrainingRoutes();

    const [translate] = useTranslation();

    const { showSharedFiles } = training || {};

    const { canReadGroups = false, canReadParticipants = false } =
        getTrainingPermissions(training);

    const sections = useMemo(
        () =>
            Object.keys(SECTIONS).filter((key) => {
                switch (key) {
                    case 'TRAINING':
                        return true;
                    case 'TRAINING_PARTICIPANTS':
                        return canReadParticipants;
                    case 'TRAINING_GROUPS':
                        return canReadGroups;
                    case 'TRAINING_TRAINERS':
                        return true;
                    case 'TRAINING_SHARED_FILES':
                        return showSharedFiles;
                }
            }),
        [showSharedFiles, canReadParticipants, canReadGroups]
    );

    const tabs = sections.map((section) => ({
        label: translate(`trainingTabs.${section}`),
        to: section,
        params: { trainingId: id },
        value: section,
    }));

    const Section = sections.includes(activeSectionRoute)
        ? SECTIONS[activeSectionRoute]
        : undefined;

    return {
        tabs,
        Section,
    };
};
